import React, { useState, useEffect } from 'react';
import { Archive, RotateCcw, Calendar, MessageSquare, Brain, FolderOpen } from 'lucide-react';
import { confirm as confirmModal } from '../ui/Toast';

const ProjectArchives = ({ onLoadProjects }) => {
  const [archives, setArchives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);

  const token = localStorage.getItem('oracle_token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const loadArchives = async () => {
    try {
      const res = await fetch('/api/projects/list/archives', { headers });
      if (res.ok) setArchives(await res.json());
    } catch (e) { console.error('Erreur archives:', e); }
    setLoading(false);
  };

  useEffect(() => { loadArchives(); }, []);

  const reactivate = async (projectId, name) => {
    if (!(await confirmModal(`Réactiver le projet "${name}" ? Il reviendra dans vos projets actifs.`))) return;
    try {
      const res = await fetch(`/api/projects/${projectId}/reactivate`, { method: 'POST', headers });
      if (res.ok) {
        setMsg({ text: `"${name}" réactivé avec succès`, type: 'success' });
        loadArchives();
        if (onLoadProjects) onLoadProjects();
        setTimeout(() => setMsg(null), 4000);
      } else {
        const d = await res.json().catch(() => ({}));
        setMsg({ text: d.message || d.error || 'Accès insuffisant pour réactiver ce projet.', type: 'error' });
      }
    } catch (e) { setMsg({ text: 'Erreur réactivation', type: 'error' }); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

  const getIcon = (type) => {
    const icons = { development: '🚀', expertise: '🧠', commercial: '💼', team: '👥', research: '🔬', documentation: '📚' };
    return icons[type] || '📁';
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full"></div>
    </div>
  );

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {msg && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${msg.type === 'error' ? 'bg-blue-600 text-white' : 'bg-green-600 text-white'}`}>
          {msg.text}
        </div>
      )}

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <Archive size={24} className="text-gray-500" />
          Projets archivés
        </h1>
        <p className="text-gray-500 mt-1">
          {archives.length} projet{archives.length > 1 ? 's' : ''} archivé{archives.length > 1 ? 's' : ''}
          {archives.length > 0 && ' — La mémoire persistante est préservée. Réactivez pour reprendre.'}
        </p>
      </div>

      {archives.length === 0 ? (
        <div className="text-center py-16">
          <FolderOpen size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-500 mb-2">Aucun projet archivé</h3>
          <p className="text-gray-400 text-sm">Les projets archivés apparaîtront ici.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {archives.map(project => (
            <div
              key={project.id}
              className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1">
                  <span className="text-2xl mt-1">{getIcon(project.project_type)}</span>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 text-lg">{project.name}</h3>
                    {project.description && (
                      <p className="text-gray-500 text-sm mt-1">{project.description}</p>
                    )}

                    {/* Stats */}
                    <div className="flex flex-wrap gap-4 mt-3 text-sm text-gray-500">
                      <span className="flex items-center gap-1.5">
                        <Calendar size={14} />
                        Archivé le {formatDate(project.archived_at)}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <MessageSquare size={14} />
                        {project.conversations_count || 0} conversations
                      </span>
                      {project.sessions_count > 0 && (
                        <span className="flex items-center gap-1.5">
                          <MessageSquare size={14} />
                          {project.sessions_count} sessions
                        </span>
                      )}
                      {project.summaries_count > 0 && (
                        <span className="flex items-center gap-1.5">
                          <Brain size={14} className="text-green-500" />
                          {project.summaries_count} résumé{project.summaries_count > 1 ? 's' : ''} MP
                        </span>
                      )}
                    </div>

                    {/* Période */}
                    {project.date_range_start && (
                      <div className="text-xs text-gray-400 mt-2">
                        Période : {formatDate(project.date_range_start)} → {formatDate(project.date_range_end)}
                      </div>
                    )}
                  </div>
                </div>

                {/* Bouton Réactiver */}
                <button
                  onClick={() => reactivate(project.id, project.name)}
                  className="flex items-center gap-2 px-4 py-2 bg-green-50 hover:bg-green-100 text-green-700 border border-green-200 rounded-lg text-sm font-medium transition-colors flex-shrink-0 ml-4"
                >
                  <RotateCcw size={15} />
                  Réactiver
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProjectArchives;
